import type { PayloadAction } from '@reduxjs/toolkit';
import type {
  QualityCheckType,
  QualityCheckTypeInitStateType,
} from 'src/redux/quality-check/types';

const reducers = {
  updateExpandedIssueId: (state: QualityCheckTypeInitStateType, action: PayloadAction<string>) => {
    state.expandedIssueId = action.payload;
  },

  // Before/after media are two independent DropZoneFileUploader instances (see
  // TripDetails.tsx) each running their own isAnyMediaProcessing check. A single
  // shared flag would let one side's "finished" overwrite the other's "still
  // uploading" — keep them as separate fields so neither can clobber the other.
  updateIsUploadingMedia: (
    state: QualityCheckTypeInitStateType,
    action: PayloadAction<{ mediaType: 'before' | 'after'; isUploading: boolean }>,
  ) => {
    if (action.payload.mediaType === 'before') {
      state.isUploadingBeforeMedia = action.payload.isUploading;
    } else {
      state.isUploadingAfterMedia = action.payload.isUploading;
    }
  },

  updateTripStatus: (
    state: QualityCheckTypeInitStateType,
    action: PayloadAction<{ trip_id: string; status: { label: string; value: string } }>,
  ) => {
    const { trip_id: tripId, status } = action.payload;
    const { expandedIssueId } = state;
    state.expandedIssueSource[expandedIssueId].tripSource = {
      ...(state.expandedIssueSource[expandedIssueId].tripSource || {}),
      [tripId]: {
        ...(state.expandedIssueSource[expandedIssueId].tripSource?.[tripId] || {}),
        status,
      },
    };
  },

  updateQCMaterialOnPusher: (
    state: QualityCheckTypeInitStateType,
    action: PayloadAction<{
      work_order_issue_uuid: string;
      material_id: string;
      label: string;
      quantity: number;
      quantity_type: string | null;
      cost_type: 'per-unit' | 'total';
      total_cost_in_cents: number;
      cost_in_cents?: number;
      unit_price_in_cents?: number;
      markup_fee_type: 'percentage' | 'fixed';
      markup_fee_type_value: string | null;
    }>,
  ) => {
    const { work_order_issue_uuid: workOrderIssueId, ...materialDetails } = action.payload;
    if (
      !state?.expandedIssueSource[workOrderIssueId]?.materialIds?.includes(
        materialDetails.material_id,
      )
    ) {
      state.expandedIssueSource[workOrderIssueId]?.materialIds?.push(materialDetails.material_id);
    }
    state.expandedIssueSource[workOrderIssueId].materialSource[materialDetails.material_id] =
      materialDetails;
  },

  deleteQcMaterialOnPusher: (
    state: QualityCheckTypeInitStateType,
    action: PayloadAction<{
      work_order_issue_id: string;
      material_id: string;
    }>,
  ) => {
    const { work_order_issue_id: workOrderIssueId, material_id: materialId } = action.payload;
    state.expandedIssueSource[workOrderIssueId].materialIds = state.expandedIssueSource[
      workOrderIssueId
    ]?.materialIds?.filter((id) => id !== materialId);
  },

  updateQualityCheckIssueStatus: (
    state: QualityCheckTypeInitStateType,
    action: PayloadAction<{ quality_check_status: QualityCheckType; work_order_issue_id: string }>,
  ) => {
    state.workOrderIssueSource[action.payload?.work_order_issue_id].quality_check_status =
      action.payload?.quality_check_status;
  },
};

export default reducers;
